import React, { useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { isWeekend } from '@/lib/calendar-utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CalendarDayPopup from './CalendarDayPopup'
import { formatPickupRooms } from '@/lib/plain-language'
import type { DayInfo, OccupancyDay, RateData } from '@/types/calendar'

interface OccupancyTimelineGridProps {
  /** Today AT THE PROPERTY (YYYY-MM-DD) — see calendar-utils#todayIso. */
  today: string
  daysInMonth: DayInfo[]
  occupancyByDate: Record<string, OccupancyDay>
  propertyOccupancyByDate?: Record<string, { booked: number; available: number; occupancyPct: number; label?: string | null }>
  currency?: string
  rateByDate?: Record<string, RateData>
  onCellClick?: (date: string) => void
  adrData?: Record<string, { adr: number | null; lastYearAdr: number | null }>
}

function occupancyBg(pct: number): string {
  if (pct >= 80) return 'bg-emerald-50/80'
  if (pct >= 60) return 'bg-amber-50/80'
  return ''
}

function occupancyTextColor(pct: number): string {
  if (pct >= 80) return 'text-emerald-700'
  if (pct >= 60) return 'text-amber-700'
  return 'text-foreground'
}

function OccupancyTimelineGrid({
  daysInMonth,
  occupancyByDate,
  propertyOccupancyByDate,
  currency,
  rateByDate,
  onCellClick,
  adrData,
  today: propertyToday,
}: OccupancyTimelineGridProps) {
  const isPast = (dateStr: string) => dateStr < propertyToday
  const todayRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    todayRef.current?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' })
  }, [])

  return (
    <TooltipProvider delayDuration={800} skipDelayDuration={0}>
      <div className="rounded-xl border border-border/60 bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[100px_1fr_1fr_1fr_1fr_1fr_1fr] border-b border-border/40">
          <div className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground border-r border-border/40">
            Date
          </div>
          <div className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center border-r border-border/30">
            Occupancy
          </div>
          <div className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center border-r border-border/30">
            Booked
          </div>
          <div className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center border-r border-border/30">
            Available
          </div>
          <div className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center border-r border-border/30">
            Pickup (7d)
          </div>
          <div className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center border-r border-border/30">
            Pickup (30d)
          </div>
          <div className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">
            Pickup (60d)
          </div>
        </div>

        {/* Rows */}
        {daysInMonth.map((day) => {
          const occ = occupancyByDate[day.date]
          const weekend = isWeekend(day.dayOfWeek)
          const today = day.date === propertyToday

          return (
            <div key={day.date} ref={today ? todayRef : undefined}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  onClick={() => onCellClick?.(day.date)}
                  className={cn(
                    'grid grid-cols-[100px_1fr_1fr_1fr_1fr_1fr_1fr] border-b border-border/30 last:border-b-0 transition-colors cursor-pointer hover:bg-muted/30',
                    isPast(day.date) && 'opacity-40',
                    today && 'bg-primary/5',
                    weekend && !today && 'bg-slate-50/50'
                  )}
                >
                  {/* Date cell */}
                  <div className="sticky left-0 z-10 bg-inherit px-3 py-2.5 border-r border-border/40 flex items-center gap-1.5">
                    <span className={cn(
                      'text-xs font-medium',
                      weekend ? 'text-primary/70' : 'text-muted-foreground'
                    )}>
                      {day.dayOfWeek}
                    </span>
                    <span className={cn(
                      'text-sm font-semibold',
                      today ? 'text-primary' : 'text-foreground'
                    )}>
                      {day.dayOfMonth}
                    </span>
                  </div>

                  {occ ? (
                    <>
                      {/* Occupancy % with bar */}
                      <div className={cn(
                        'px-3 py-2.5 border-r border-border/30 flex items-center justify-center gap-2',
                        occupancyBg(occ.occupancyPct)
                      )}>
                        <div className="w-16 h-2 rounded-full bg-slate-200 overflow-hidden">
                          <div
                            className={cn(
                              'h-full rounded-full transition-all',
                              occ.occupancyPct >= 80 ? 'bg-emerald-500' : occ.occupancyPct >= 60 ? 'bg-amber-500' : 'bg-slate-400'
                            )}
                            style={{ width: `${Math.min(occ.occupancyPct, 100)}%` }}
                          />
                        </div>
                        <span className={cn('text-sm font-bold tabular-nums', occupancyTextColor(occ.occupancyPct))}>
                          {occ.occupancyPct}%
                        </span>
                      </div>

                      {/* Booked */}
                      <div className="px-3 py-2.5 border-r border-border/30 text-center">
                        <span className="text-sm font-semibold tabular-nums">{occ.booked}</span>
                      </div>

                      {/* Available */}
                      <div className="px-3 py-2.5 border-r border-border/30 text-center">
                        <span className="text-sm tabular-nums text-muted-foreground">{occ.available}</span>
                      </div>

                      {/* Pickup 7d — rooms, not points (docs/revenue-vocabulary.md § Plain-language) */}
                      <div className="px-3 py-2.5 border-r border-border/30 text-center">
                        <span className={cn(
                          'text-sm font-semibold tabular-nums',
                          occ.pickup7dRooms != null && occ.pickup7dRooms > 0 ? 'text-emerald-600' : occ.pickup7dRooms != null && occ.pickup7dRooms < 0 ? 'text-rose-600' : 'text-muted-foreground'
                        )}>
                          {formatPickupRooms(occ.pickup7dRooms)}
                        </span>
                      </div>

                      {/* Pickup 30d */}
                      <div className="px-3 py-2.5 border-r border-border/30 text-center">
                        <span className={cn(
                          'text-sm font-semibold tabular-nums',
                          occ.pickup30dRooms != null && occ.pickup30dRooms > 0 ? 'text-emerald-600' : occ.pickup30dRooms != null && occ.pickup30dRooms < 0 ? 'text-rose-600' : 'text-muted-foreground'
                        )}>
                          {formatPickupRooms(occ.pickup30dRooms)}
                        </span>
                      </div>

                      {/* Pickup 60d */}
                      <div className="px-3 py-2.5 text-center">
                        <span className={cn(
                          'text-sm font-semibold tabular-nums',
                          occ.pickup60dRooms != null && occ.pickup60dRooms > 0 ? 'text-emerald-600' : occ.pickup60dRooms != null && occ.pickup60dRooms < 0 ? 'text-rose-600' : 'text-muted-foreground'
                        )}>
                          {formatPickupRooms(occ.pickup60dRooms)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="col-span-6 px-3 py-2.5 text-center text-muted-foreground/40">
                      &mdash;
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                sideOffset={4}
                className="max-w-none overflow-visible border-none bg-transparent p-0 shadow-none"
              >
                <CalendarDayPopup
                  today={propertyToday}
                  date={day.date}
                  dayOfWeek={day.dayOfWeek}
                  currency={currency || 'GBP'}
                  rate={rateByDate?.[day.date]}
                  occupancy={occ ? {
                    booked: occ.booked,
                    available: occ.available,
                    occupancyPct: occ.occupancyPct,
                    pickup7d: occ.pickup7d,
                    pickup7dRooms: occ.pickup7dRooms,
                  } : undefined}
                  propertyOccupancy={propertyOccupancyByDate?.[day.date]}
                  adr={adrData?.[day.date]?.adr}
                  lastYearAdr={adrData?.[day.date]?.lastYearAdr}
                />
              </TooltipContent>
            </Tooltip>
            </div>
          )
        })}
      </div>
    </TooltipProvider>
  )
}

export default React.memo(OccupancyTimelineGrid)
